import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { getKPI, getAgenteDashboard, getAziendaDashboard } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollText, QrCode, Building2, Users, CheckCircle, Clock, AlertCircle, Euro, Percent, FileText, ExternalLink } from 'lucide-react';

const DashboardPage = () => {
  const { user, isAdmin, isAgente, isAzienda } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isAdmin()) {
          const response = await getKPI();
          setKpi(response.data);
        } else if (isAgente()) {
          const response = await getAgenteDashboard();
          setKpi(response.data);
        } else if (isAzienda()) {
          const response = await getAziendaDashboard();
          setKpi(response.data);
        }
      } catch (error) {
        console.error('Error loading KPI:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [isAdmin, isAgente, isAzienda]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const getRuoloLabel = () => {
    switch(user?.ruolo) {
      case 'admin': return 'Amministratore';
      case 'agente': return 'Agente';
      case 'azienda': return 'Azienda';
      default: return '';
    }
  };

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <h1 
          className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Dashboard
        </h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">
          Benvenuto, {user?.nome} {user?.cognome} - {getRuoloLabel()}
        </p>
      </div>

      {/* Admin KPI */}
      {isAdmin() && kpi && (
        <div data-testid="dashboard-stats">
          {/* Elogi Section */}
          <h2 className="text-base sm:text-lg font-medium text-slate-800 mb-3 sm:mb-4">Elogi</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <Card data-testid="kpi-elogi-totali">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Elogi Totali</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <ScrollText className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-slate-900">{kpi.elogi_totali}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200" data-testid="kpi-elogi-attivi">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Elogi Attivi</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-green-100">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-green-600">{kpi.elogi_attivi}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200" data-testid="kpi-elogi-da-attivare">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Da Attivare</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-amber-100">
                    <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-amber-600">{kpi.elogi_da_attivare}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-300" data-testid="kpi-elogi-senza-qr">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Senza QR</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-slate-500">{kpi.elogi_senza_qr}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* QR Section */}
          <h2 className="text-base sm:text-lg font-medium text-slate-800 mb-3 sm:mb-4">QR Code</h2>
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <Card data-testid="kpi-qr-totali">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">QR Totali</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <QrCode className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-slate-900">{kpi.qr_totali}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200" data-testid="kpi-qr-attivi">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">QR Attivi</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-green-100">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-green-600">{kpi.qr_attivi}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-300" data-testid="kpi-qr-disponibili">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Disponibili</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <QrCode className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-slate-500">{kpi.qr_disponibili}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Aziende Section */}
          <h2 className="text-base sm:text-lg font-medium text-slate-800 mb-3 sm:mb-4">Aziende</h2>
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <Card data-testid="kpi-aziende-totali">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Totali</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-slate-900">{kpi.aziende_totali}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200" data-testid="kpi-aziende-con-qr">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Con QR</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-green-100">
                    <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-green-600">{kpi.aziende_con_qr}</span>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="kpi-agenti-totali">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Agenti</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <Users className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-slate-900">{kpi.agenti_totali}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Agente KPI */}
      {isAgente() && kpi && (
        <div data-testid="dashboard-stats">
          {/* Sezione Documentazione per Agenti */}
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Documentazione Agenti
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Consulta guide, procedure e materiali formativi
                    </p>
                  </div>
                </div>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                  onClick={() => window.open('https://drive.google.com/drive/folders/1XYlCZG09fNXFMJOAdtxF3nix3jnVd3wL?usp=sharing', '_blank')}
                  data-testid="documentation-link-dashboard"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Apri Documentazione
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Sezione Elogio Demo per Agenti */}
          <Card className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <ScrollText className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Elogio Dimostrativo
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Crea un elogio di esempio per mostrare ai clienti la facilità e semplicità d'uso
                    </p>
                  </div>
                </div>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
                  onClick={() => window.location.href = '/elogi?demo=true'}
                  data-testid="create-demo-elogio-btn"
                >
                  <ScrollText className="h-4 w-4 mr-2" />
                  Crea Elogio Demo
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Commissioni Section */}
          <h2 className="text-base sm:text-lg font-medium text-slate-800 mb-3 sm:mb-4">Commissioni</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <Card data-testid="kpi-agente-percentuale">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">% Comm.</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-sky-100">
                    <Percent className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-sky-600">{kpi.commissione_percentuale}%</span>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="kpi-agente-maturate">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Maturate</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <Euro className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                  </div>
                  <span className="text-lg sm:text-2xl font-bold text-slate-900">
                    {kpi.commissioni_maturate?.toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200" data-testid="kpi-agente-pagate">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Pagate</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-green-100">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <span className="text-lg sm:text-2xl font-bold text-green-600">
                    {kpi.commissioni_pagate?.toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200" data-testid="kpi-agente-da-pagare">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Da Pagare</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-amber-100">
                    <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                  </div>
                  <span className="text-lg sm:text-2xl font-bold text-amber-600">
                    {kpi.commissioni_da_pagare?.toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Aziende Section */}
          <h2 className="text-base sm:text-lg font-medium text-slate-800 mb-3 sm:mb-4">Le tue Aziende</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <Card data-testid="kpi-agente-aziende">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Totali</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-slate-900">{kpi.aziende_totali}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200" data-testid="kpi-agente-aziende-attive">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Attive</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-green-100">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-green-600">{kpi.aziende_attive}</span>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="kpi-agente-qr-totali">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">QR Tot.</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <QrCode className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-slate-900">{kpi.qr_totali}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200" data-testid="kpi-agente-qr-attivi">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">QR Attivi</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-green-100">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-green-600">{kpi.qr_attivi}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Azienda KPI */}
      {isAzienda() && kpi && (
        <div data-testid="dashboard-stats">
          {/* Elogi Section */}
          <h2 className="text-base sm:text-lg font-medium text-slate-800 mb-3 sm:mb-4">I tuoi Elogi</h2>
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <Card data-testid="kpi-azienda-elogi">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Totali</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <ScrollText className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-slate-900">{kpi.elogi_totali}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200" data-testid="kpi-azienda-elogi-attivi">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Attivi</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-green-100">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-green-600">{kpi.elogi_attivi}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200" data-testid="kpi-azienda-in-lavorazione">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">In Lav.</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-amber-100">
                    <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-amber-600">{kpi.elogi_in_lavorazione}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* QR Section */}
          <h2 className="text-base sm:text-lg font-medium text-slate-800 mb-3 sm:mb-4">I tuoi QR Code</h2>
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <Card data-testid="kpi-azienda-qr-totali">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Totali</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <QrCode className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-slate-900">{kpi.qr_totali}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200" data-testid="kpi-azienda-qr-attivi">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Attivi</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-green-100">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-green-600">{kpi.qr_attivi}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-300" data-testid="kpi-azienda-qr-disponibili">
              <CardHeader className="pb-2 p-3 sm:p-6 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-500">Disp.</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100">
                    <QrCode className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                  </div>
                  <span className="text-xl sm:text-3xl font-bold text-slate-500">{kpi.qr_disponibili}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
